import { fetchServer } from "@/features/utils/fetch-server";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface PublicCustomer {
  id: string;
  given_name: string;
  family_name: string;
  phone: string;
  email: string;
  phone_supports_sms: boolean;
  email_is_verified: boolean;
  phone_is_verified: boolean;
  // Contact proven by the transport layer (calling number, OTP-verified phone, inbound
  // email address): read-only, the backend refuses to overwrite it.
  phone_is_locked: boolean;
  email_is_locked: boolean;
  // When they accepted the privacy policy and terms (0 = never). Consent is asked once,
  // not on every visit to the form.
  terms_accepted_at?: number;
}

export interface UpdateCustomerInput {
  given_name?: string;
  family_name?: string;
  phone?: string;
  email?: string;
  accept_terms?: boolean;
}

export interface UpdateCustomerError {
  error: string;
  feedback?: Record<string, string>;
}

// Fetch customer info for a public session
export function usePublicCustomer(publicId: string) {
  return useQuery({
    queryKey: ["public-customer", publicId],
    queryFn: async (): Promise<PublicCustomer> => {
      const response = await fetchServer(
        `/api/sessions/v1/public/${publicId}/customer`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch customer");
      }

      return response.json();
    },
    enabled: !!publicId,
  });
}

// Update customer info for a public session
export function useUpdatePublicCustomer(publicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: UpdateCustomerInput,
    ): Promise<{ success: boolean }> => {
      const response = await fetchServer(
        `/api/sessions/v1/public/${publicId}/customer`,
        {
          method: "POST",
          credentials: "include",
          // The refusal is read below: its `feedback` says which field was wrong, and
          // `fetchServer` would otherwise throw it away for its own generic error.
          ignoreError: true,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );

      if (!response.ok) {
        const error: UpdateCustomerError = await response.json();
        throw error;
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate customer query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: ["public-customer", publicId],
      });
    },
  });
}
